import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import { cn } from '../../../lib/utils';
import { riseInItem } from './motion';

/**
 * BentoGrid — adapted from the Aceternity UI bento-grid, re-homed onto this design
 * system's tokens. The STRUCTURE is the source's: an auto-flow grid of cards, each a
 * flex column that pushes `header` to the top and the text block to the bottom, with a
 * `group/bento` hover that lifts the shadow and nudges the text right.
 *
 * FOUR DELIBERATE DEPARTURES from the upstream copy, each forced by this codebase:
 *
 *  1. COLOURS. Upstream hardcodes `bg-white` / `text-neutral-600` / `border-neutral-200`
 *     plus `dark:` variants. Theming here runs on CSS custom properties, and
 *     `darkMode` is not configured in tailwind.config.ts — so every `dark:` utility
 *     would be inert and the card would be locked to light mode. The tokens
 *     (surface / text / border-subtle) already carry both themes, so they replace the
 *     literals outright and the `dark:` pairs are dropped rather than ported dead.
 *
 *  2. GRID TEMPLATE. Upstream pins `md:grid-cols-3` + `md:auto-rows-[18rem]`. This uses
 *     `grid-cols-cards` — repeat(auto-fill, minmax(var(--card-min-width), 1fr)) — so
 *     tiles flow to fit and collapse to one column under 645px, where the token becomes
 *     100%. That is the same responsive contract MyToolCollection and ToolCard already
 *     follow; a fixed `md:` breakpoint is what the profile page's own comment calls a
 *     "dead `md:` breakpoint".
 *
 *  3. SHADOW. Upstream's resting `shadow-input` is not a token in this config. Resting
 *     elevation here is a 1px `border-subtle` line, so the card rests flat and gains
 *     the MenuBar's token-based elevation on hover — the state change survives, the undefined
 *     class does not. Tailwind's default `shadow-lg` is a hardcoded grey and is NOT used anywhere
 *     in this design system, so the hover reuses --accent-subtle exactly as MenuBar.tsx does.
 *
 *  4. MOTION. Upstream is transition-only. This wires each item to `riseInItem` so a
 *     grid wrapped in `staggerContainer()` plays the same entrance as every other
 *     card surface here. `motion` (v11, imported as `motion/react`) was already a
 *     dependency; `clsx` + `tailwind-merge` were added for `cn`.
 *
 * The radius is `rounded-md` (the dominant 8px) and the padding `p-card`, matching ToolCard, so a bento
 * item and a tool tile sit in the same grid without disagreeing on geometry.
 */
export const BentoGrid = ({
  className,
  children,
}: {
  className?: string;
  children?: ReactNode;
}) => {
  return <div className={cn('grid grid-cols-cards gap-5', className)}>{children}</div>;
};

export interface BentoGridItemProps {
  className?: string;
  title?: ReactNode;
  description?: ReactNode;
  /** Top block — a thumbnail, chart, or any visual. Fills the space above the text. */
  header?: ReactNode;
  /** Small mark above the title, typically an icon. */
  icon?: ReactNode;
}

export const BentoGridItem = ({
  className,
  title,
  description,
  header,
  icon,
}: BentoGridItemProps) => {
  return (
    <motion.div
      variants={riseInItem}
      className={cn(
        // `group/bento` is a NAMED group: the text block below keys its hover off
        // `group-hover/bento`, so a nested group inside `header` cannot capture it.
        'group/bento flex flex-col justify-between gap-4 rounded-md border border-subtle bg-surface p-card',
        'transition-shadow duration-micro ease-out hover:shadow-[0px_2px_3px_2px_var(--accent-subtle)]',
        className,
      )}
    >
      {header}
      <div className="transition-transform duration-micro ease-out group-hover/bento:translate-x-1">
        {icon}
        {title != null && (
          <div className="mt-2 mb-1 font-sans text-sm font-medium text-text">{title}</div>
        )}
        {description != null && (
          <div className="font-sans text-xs font-light text-text-inactive">{description}</div>
        )}
      </div>
    </motion.div>
  );
};
